import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { Alert, AlertDescription, Text } from "@zapier/design-system";
import {
  isTourAlertVisibleAtom,
  emitUserInteractionEventSetter,
} from "@zapier/canvas-state";
import styles from "./TourAlert.module.css";

/**
 * Toast-style alert component for tour-related notifications.
 *
 * Features:
 * - Shows "You can start the tour at any time from the Help menu." message
 * - Auto-dismisses after 5 seconds
 * - Manual dismiss via close button
 * - Uses design system Alert with toast variant
 * - Only renders when alert is visible
 *
 * Commonly triggered by:
 * - Tour exit actions (both exit and done)
 */
export function TourAlert() {
  const showTourAlert = useAtomValue(isTourAlertVisibleAtom);
  const setIsTourAlertVisible = useSetAtom(isTourAlertVisibleAtom);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);

  const handleDismiss = async () => {
    setIsTourAlertVisible(false);
    await emitUserInteractionEvent({
      eventAction: "click",
      interactionGoal: "Tour: clicked dismiss alert",
      rawText: "dismiss tour alert",
    });
  };

  // Auto-dismiss timer
  useEffect(() => {
    if (showTourAlert) {
      const timer = setTimeout(async () => {
        setIsTourAlertVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showTourAlert, setIsTourAlertVisible]);

  if (!showTourAlert) {
    return null;
  }

  return (
    <div className={styles.tourAlertWrapper}>
      <Alert onDismiss={handleDismiss} status="info" variant="toast">
        <AlertDescription>
          <Text type="Body3">
            You can start the tour at any time from the Help menu.
          </Text>
        </AlertDescription>
      </Alert>
    </div>
  );
}
